// Lightweight, privacy-conscious client telemetry.
//
// Two things reported to the backend for the admin analytics dashboard:
//   1. PWA install events (via the browser's `appinstalled` event)
//   2. Uncaught JavaScript errors (window.onerror, unhandledrejection, and
//      failed axios responses with status >= 500)
//
// No user content is ever captured — only error messages, status codes, and
// the path (query-stripped) where the error occurred.

import { api } from "@/lib/api";

let installed = false;

const detectPlatform = () => {
  if (typeof navigator === "undefined") return "unknown";
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/android/.test(ua)) return "android";
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/windows/.test(ua)) return "windows";
  if (/mac os/.test(ua)) return "macos";
  if (/linux/.test(ua)) return "linux";
  if (/cros/.test(ua)) return "chromeos";
  return "unknown";
};

const post = (path, body) => {
  // fire-and-forget; we don't want telemetry failures to crash the app
  try {
    api.post(path, body).catch(() => {});
  } catch (_) {
    /* noop */
  }
};

const reportError = ({ message, source, stack, status, method }) => {
  if (!message) return;
  post("/analytics/client-error", {
    message: String(message).slice(0, 500),
    source: source || "client",
    stack: stack ? String(stack).slice(0, 1500) : undefined,
    url: typeof window !== "undefined" ? window.location.pathname : undefined,
    status: typeof status === "number" ? status : undefined,
    method: method || undefined,
  });
};

export const initTelemetry = () => {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // PWA installed confirmation — fires once per install on supported browsers.
  window.addEventListener("appinstalled", () => {
    post("/analytics/pwa-install", {
      platform: detectPlatform(),
      user_agent: navigator.userAgent?.slice(0, 200),
    });
  });

  // Uncaught script errors
  window.addEventListener("error", (ev) => {
    reportError({
      message: ev?.message || "unknown error",
      source: "window.onerror",
      stack: ev?.error?.stack,
    });
  });

  // Unhandled promise rejections
  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev?.reason;
    const msg = reason?.message || String(reason || "unhandled rejection");
    reportError({
      message: msg,
      source: "unhandledrejection",
      stack: reason?.stack,
    });
  });

  // Axios 5xx responses — treat as technical errors (4xx are auth/validation
  // noise, we don't want them drowning the signal).
  api.interceptors.response.use(
    (r) => r,
    (err) => {
      const status = err?.response?.status;
      if (status >= 500) {
        reportError({
          message: `${err?.config?.method?.toUpperCase() || "REQ"} ${err?.config?.url || ""} → ${status}`,
          source: "axios",
          status,
          method: err?.config?.method?.toUpperCase(),
        });
      }
      return Promise.reject(err);
    },
  );
};
